import { Store } from '@ngrx/store';
import { KeycloakService } from 'keycloak-angular';
import { Observable, from, map, switchMap } from 'rxjs';

import { isKioskUrl } from '@core/helpers/is-kiosk-url';
import { persistKioskTokens } from '@core/helpers/kiosk-token-persistence';
import { persistTokens } from '@core/helpers/token-persistence';
import { setUpdatedTokenAndPermissions } from '@core/stores/auth/auth.actions';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';

import { isDefined } from '@shared/utils/is-defined';
import { getRefreshTokenExpiresDateTime } from '@shared/utils/refresh-token-expires';

// Matches keycloak-angular's default _updateMinValidity
const UPDATE_TOKEN_MIN_VALIDITY_SECONDS = 20;

// Calls keycloak-js directly because the keycloak-angular wrapper swallows refresh failures and
// resolves false instead of rejecting. Errors when the token refresh fails (e.g. expired refresh
// token); emits null when the refresh produced no token.
export const refreshAccessToken$ = (keycloakService: KeycloakService, store: Store): Observable<string | null> => {
  return from(keycloakService.getKeycloakInstance().updateToken(UPDATE_TOKEN_MIN_VALIDITY_SECONDS)).pipe(
    switchMap(() => {
      return from(keycloakService.getToken());
    }),
    map((token) => {
      if (!token) {
        return null;
      }

      const instance = keycloakService.getKeycloakInstance();
      const permissions = keycloakService.getUserRoles(false) as PermissionStoreModelEnum[];
      const name = instance.tokenParsed?.['preferred_username'];
      const refreshTokenExpires = getRefreshTokenExpiresDateTime(keycloakService);

      if (instance.token && instance.refreshToken && instance.idToken && isDefined(instance.timeSkew)) {
        if (isKioskUrl()) {
          persistKioskTokens(instance.token, instance.refreshToken, instance.idToken, instance.timeSkew);
        } else {
          persistTokens(instance.token, instance.refreshToken, instance.idToken, instance.timeSkew);
        }
      }

      store.dispatch(
        setUpdatedTokenAndPermissions({
          token,
          refreshTokenExpiresTimestamp: refreshTokenExpires && refreshTokenExpires.toISO(),
          permissions,
          name,
          organisationId: instance.tokenParsed?.['organization']?.[0],
        })
      );

      return token;
    })
  );
};
